import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useOrders } from './useOrders';
import type { SyncOrdersResponse } from '../services/b2bSyncApi';
import { formatOrderSyncMessage } from '../utils/orderUtils';
import {
  buildResultMessage,
  formatRevolutReconcileResult,
  formatRevolutMerchantImportResult,
  revolutReconcileProblems,
  formatBraintreeReconcileResult,
  braintreeReconcileProblems,
  formatPaypalReconcileResult,
  formatVascoReconcileResult,
  formatBankTransferMatchResult,
  paypalSettlementProblems,
  previewProblemBlock,
} from '../utils/reconcileMessages';
import type { ResultMessage } from '../utils/reconcileMessages';
import type {
  MatchResult,
  ReconcileResult,
  BraintreeReconcileResult,
  BankPaymentUploadResult,
  PaypalReconcileResult,
  VascoReconcileResult,
  RevolutApiStatus,
  RevolutApiImportResult,
  RevolutMerchantApiStatus,
  RevolutMerchantImportResult,
  GkRatesSyncResult,
} from '../components/accounting/types';

const BACKFILL_AFTER = '2025-08-31T23:59:59';
const BACKFILL_START_LABEL = '2025-09-01';

function getBackfillErrorMessage(err: any): string {
  const response = err?.response;
  const data = response?.data;
  const serverMessage = typeof data?.message === 'string' ? data.message : '';
  const serverError = typeof data?.error === 'string' ? data.error : '';

  if (serverMessage && serverError) return `${serverError}: ${serverMessage}`;
  if (serverMessage) return serverMessage;
  if (serverError) return serverError;
  if (response?.status) return `Backfill failed with HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;
  if (err?.message) return err.message;

  return 'Backfill failed';
}

// Shown when a waiting result comes without notes. The server normally sends this text itself.
const MERCHANT_REPORT_WAITING_NOTE = 'Revolut is still preparing the report. Click "Revolut CC" again in a minute to continue.';

/**
 * Turn an axios upload error into an actionable message. A "relatively big"
 * CSV that imports fine locally but fails instantly in production is almost
 * always the reverse proxy rejecting the request body before it reaches the
 * app — nginx's default `client_max_body_size` is only 1 MB and returns a 413
 * with an HTML page, so there's no JSON `error` field and the bare fallback
 * ("…failed") hides the real cause. Surface that explicitly.
 */
export function getUploadErrorMessage(err: any, fallback: string): string {
  const serverError = err?.response?.data?.error;
  if (typeof serverError === 'string' && serverError) return serverError;
  if (err?.response?.status === 413) {
    return 'File too large for the server upload limit. Ask an admin to raise the reverse proxy body-size limit (e.g. nginx client_max_body_size).';
  }
  if (!err?.response) {
    return 'Could not reach the server — the upload may have been blocked by a proxy size/timeout limit or the connection dropped.';
  }
  return fallback;
}

export type OverwritePhase = 'previewing' | 'confirm' | 'overwriting';

export interface OverwriteState {
  kind: 'revolut' | 'braintree';
  file: File;
  phase: OverwritePhase;
  title: string;
  message?: string; // set once phase === 'confirm'
  pct: number; // 0..100, time-estimate progress for the active phase
  estUnits?: number; // workload units (from the preview) used to size the overwrite estimate
}

// Time-estimate progress model. The real cost is server-side row-by-row work we
// can't observe from a single request, so we animate a determinate bar over an
// estimated duration instead. The curve approaches an asymptote (never 100%
// until the request actually completes), so over/under-estimates degrade
// gracefully — it keeps creeping rather than stalling or finishing early.
const PROGRESS_BASE_MS = 1500;
const PROGRESS_PER_UNIT_MS = 7;
const PROGRESS_CAP = 0.97;

/** Estimated duration (ms) for `units` of server-side work. */
export function estimateMs(units: number): number {
  return PROGRESS_BASE_MS + PROGRESS_PER_UNIT_MS * Math.max(0, units);
}

/** Fraction (0..PROGRESS_CAP) of estimated work done after `elapsedMs`. */
export function estimateProgress(elapsedMs: number, estMs: number): number {
  const tau = Math.max(400, estMs) / 1.9; // reaches ~85% at elapsed === estMs
  return Math.min(PROGRESS_CAP, 1 - Math.exp(-Math.max(0, elapsedMs) / tau));
}

/** Rough server-work units when we have no counts yet (preview phase): ~rows. */
function estimateUnitsFromFile(file: File): number {
  return file.size / 130; // ≈ average CSV row size in bytes
}

export interface AccountingActionsReturn {
  uploading: boolean;
  reconciling: boolean;
  matching: boolean;
  syncing: boolean;
  backfilling: boolean;
  success: string;
  /** Finished, but the user has to act (shown orange). */
  warning: string;
  error: string;
  setSuccess: (msg: string) => void;
  setWarning: (msg: string) => void;
  setError: (msg: string) => void;
  revolutApi: RevolutApiStatus | null;
  revolutMerchantApi: RevolutMerchantApiStatus | null;
  handleRevolutMerchantImport: () => Promise<void>;
  handleUploadXml: (file: File) => Promise<void>;
  handleReconcileRevolut: (file: File) => Promise<void>;
  handleReconcileRevolutOverwrite: (file: File) => Promise<void>;
  handleReconcileBraintree: (file: File) => Promise<void>;
  handleReconcileBraintreeOverwrite: (file: File) => Promise<void>;
  handleReconcilePaypal: (file: File) => Promise<void>;
  handleReconcileVasco: (file: File) => Promise<void>;
  handleBankTransferMatch: () => Promise<void>;
  handleSync: () => Promise<void>;
  handleBackfillOrders: () => Promise<void>;
  overwrite: OverwriteState | null;
  confirmOverwrite: () => Promise<void>;
  cancelOverwrite: () => void;
}

export function useAccountingActions(refetchFn: () => void): AccountingActionsReturn {
  const [uploading, setUploading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [matching, setMatching] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');
  const [overwrite, setOverwrite] = useState<OverwriteState | null>(null);
  const [revolutApi, setRevolutApi] = useState<RevolutApiStatus | null>(null);
  const [revolutMerchantApi, setRevolutMerchantApi] = useState<RevolutMerchantApiStatus | null>(null);

  const { syncing, handleSyncOrders } = useOrders({ autoSync: false });

  // Decides whether "Sync" fetches new payments from the Revolut API. When the
  // server has it switched off, Sync skips this step and the user imports a
  // camt.053 XML file instead.
  const loadRevolutApiStatus = useCallback(async () => {
    try {
      const response = await axios.get<RevolutApiStatus>('/api/accounting/revolut-api/status');
      setRevolutApi(response?.data ?? null);
    } catch {
      setRevolutApi(null);
    }
  }, []);

  // Decides what "Reconcile ▾ → Revolut CC" does: the Revolut Merchant API
  // import when the server has it switched on, otherwise the CSV file picker.
  const loadRevolutMerchantApiStatus = useCallback(async () => {
    try {
      const response = await axios.get<RevolutMerchantApiStatus>('/api/accounting/revolut-merchant-api/status');
      setRevolutMerchantApi(response?.data ?? null);
    } catch {
      setRevolutMerchantApi(null);
    }
  }, []);

  useEffect(() => {
    loadRevolutApiStatus();
    loadRevolutMerchantApiStatus();
  }, [loadRevolutApiStatus, loadRevolutMerchantApiStatus]);

  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  // A success message stays until the user closes it or leaves the page.
  // Each action clears the messages of the previous action when it starts.
  const clearMessages = () => {
    setSuccess('');
    setWarning('');
    setError('');
  };

  // Orange when the import left something to do, green otherwise.
  const showResult = ({ message, warning: isWarning }: ResultMessage) => {
    if (isWarning) setWarning(message);
    else setSuccess(message);
  };

  const stopProgress = () => {
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
  };

  // Animate overwrite.pct toward the asymptote over the estimated duration until
  // stopped (by a phase transition or request completion).
  const startProgress = (estMs: number) => {
    stopProgress();
    const start = Date.now();
    progressRef.current = setInterval(() => {
      const pct = Math.round(estimateProgress(Date.now() - start, estMs) * 100);
      setOverwrite((prev) => (prev ? { ...prev, pct } : prev));
    }, 120);
  };

  // The requests of the automatic steps. Each returns the text for the
  // message and throws when the request fails. "Sync" runs all of them; the
  // "Reconcile ▾" menu runs some of them alone.

  const runRevolutApiImport = async (): Promise<ResultMessage> => {
    const { data } = await axios.post<RevolutApiImportResult>('/api/accounting/revolut-api/import');
    const parts = [`Imported ${data.imported} new payment${data.imported !== 1 ? 's' : ''} from Revolut`];
    if (data.skipped > 0) parts.push(`${data.skipped} already imported`);
    if (data.pendingCredits > 0) {
      parts.push(`${data.pendingCredits} still pending, the next import will add ${data.pendingCredits !== 1 ? 'them' : 'it'}`);
    }
    return buildResultMessage(parts.join(' · '), paypalSettlementProblems(data.paypalSettlements));
  };

  const runBankTransferMatch = async (): Promise<ResultMessage> => {
    const { data } = await axios.post<MatchResult>('/api/accounting/bank-transfer-match');
    return formatBankTransferMatchResult(data);
  };

  // Bank of Slovenia USD rates for the Vasco GK export. The server skips the
  // step while the GK export is switched off.
  const runGkRatesSync = async (): Promise<ResultMessage> => {
    const { data } = await axios.post<GkRatesSyncResult>('/api/accounting/gk-export/rates/sync');
    if (data.skipped) return { message: 'skipped, the GK export is not switched on.', warning: false };
    const newest = data.newestRate ? `newest rate ${data.newestRate.rate} (${data.newestRate.date})` : 'no rate stored yet';
    const parts = [data.fetched ? `fetched from the Bank of Slovenia, ${newest}` : `up to date, ${newest}`];
    const earlier = data.earlierDays ?? 0;
    if (earlier > 0 && data.earlierDaysError) {
      parts.push(`could not fetch ${earlier} earlier day${earlier !== 1 ? 's' : ''} without a rate: ${data.earlierDaysError}`);
    } else if (earlier > 0) {
      parts.push(`also fetched ${earlier} earlier day${earlier !== 1 ? 's' : ''} without a rate`);
    }
    return { message: parts.join(' · '), warning: Boolean(data.earlierDaysError) };
  };

  // The counts come first, then each note of the server on its own line.
  // While Revolut is still preparing the report, nothing is imported yet and
  // the text is only the notes. This is not an error: the next run continues
  // with the same report.
  const runRevolutMerchantImport = async (prefix = ''): Promise<ResultMessage & { waiting: boolean }> => {
    const { data } = await axios.post<RevolutMerchantImportResult>('/api/accounting/revolut-merchant-api/import');
    const notes = data.notes ?? [];
    if (data.waiting) {
      return { message: (notes.length ? notes : [MERCHANT_REPORT_WAITING_NOTE]).join('\n'), warning: false, waiting: true };
    }
    return { ...formatRevolutMerchantImportResult(data, prefix), waiting: false };
  };

  const handleUploadXml = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    clearMessages();
    try {
      const { data } = await axios.post<BankPaymentUploadResult>(
        '/api/accounting/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showResult(buildResultMessage(
        `Imported ${data.imported} payment${data.imported !== 1 ? 's' : ''}` +
          (data.skipped > 0 ? ` (${data.skipped} duplicate${data.skipped !== 1 ? 's' : ''} skipped)` : ''),
        paypalSettlementProblems(data.paypalSettlements),
      ));
      await refetchFn();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReconcileRevolut = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    setReconciling(true);
    clearMessages();
    try {
      const { data } = await axios.post<ReconcileResult>(
        '/api/accounting/revolut-reconcile',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showResult(formatRevolutReconcileResult(data, 'Reconciled: '));
      await refetchFn();
    } catch (err: any) {
      setError(getUploadErrorMessage(err, 'Reconciliation failed'));
    } finally {
      setReconciling(false);
    }
  };

  const handleRevolutMerchantImport = async () => {
    setReconciling(true);
    clearMessages();
    try {
      const { waiting, ...result } = await runRevolutMerchantImport('Revolut cards: ');
      showResult(result);
      if (!waiting) await refetchFn();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Revolut card reconciliation failed');
    } finally {
      setReconciling(false);
      loadRevolutMerchantApiStatus();
    }
  };

  // Stage 1 (preview): dry-run with overwrite to compute what would be wiped,
  // then open the in-app confirmation modal. We deliberately do NOT use
  // window.confirm here: for large files the upload + server count query can
  // outlast the browser's transient user-activation window, after which the
  // native dialog is silently suppressed (returns false) and the overwrite
  // appears to "cancel itself". The in-app modal has no such gating.
  const handleReconcileRevolutOverwrite = async (file: File) => {
    clearMessages();
    setOverwrite({
      kind: 'revolut',
      file,
      phase: 'previewing',
      title: 'Overwrite Revolut CC reconciliation',
      pct: 0,
    });
    startProgress(estimateMs(estimateUnitsFromFile(file)));
    try {
      const previewForm = new FormData();
      previewForm.append('file', file);
      const { data: preview } = await axios.post<ReconcileResult>(
        '/api/accounting/revolut-reconcile?overwrite=true&dryRun=true',
        previewForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const linksToWipe = preview.priorLinksWiped ?? 0;
      const txnsToWipe = preview.priorTransactionsWiped ?? 0;
      const paymentsToReset = preview.priorPaymentsReset ?? 0;
      const fromLabel = preview.overwriteDateFrom ? preview.overwriteDateFrom.slice(0, 10) : '?';
      const toLabel = preview.overwriteDateTo ? preview.overwriteDateTo.slice(0, 10) : '?';
      const previewLinks = preview.salesLinked ?? 0;
      const previewPayouts = preview.payoutsMatched ?? 0;
      const previewCarryovers = preview.chainsWithCarryover ?? 0;

      const message =
        `Overwrite Revolut CC reconciliation (${fromLabel} → ${toLabel})\n\n` +
        `This will DELETE ${linksToWipe} order link(s) and ${txnsToWipe} revolut_transactions row(s),\n` +
        `and RESET ${paymentsToReset} payment(s), then re-import the CSV in a single transaction.\n\n` +
        `Re-import preview: ${previewLinks} order(s) would be linked, ` +
        `${previewPayouts} payout(s) matched, ${previewCarryovers} carryover chain(s).` +
        previewProblemBlock(revolutReconcileProblems(preview));

      // The wiped-txn count is the best available proxy for the re-import workload.
      const estUnits = txnsToWipe || estimateUnitsFromFile(file);
      stopProgress();
      setOverwrite((prev) => (prev ? { ...prev, phase: 'confirm', message, estUnits, pct: 0 } : prev));
    } catch (err: any) {
      stopProgress();
      setOverwrite(null);
      setError(getUploadErrorMessage(err, 'Reconciliation failed'));
    }
  };

  // Stage 2 (actual run): executed after the user confirms in the modal.
  const runRevolutOverwrite = async (file: File) => {
    setReconciling(true);
    clearMessages();
    try {
      const liveForm = new FormData();
      liveForm.append('file', file);
      const { data } = await axios.post<ReconcileResult>(
        '/api/accounting/revolut-reconcile?overwrite=true',
        liveForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      showResult(formatRevolutReconcileResult(
        data,
        `Overwrite: wiped ${data.priorLinksWiped ?? 0} link(s), ${data.priorTransactionsWiped ?? 0} txn(s), reset ${data.priorPaymentsReset ?? 0} payment(s) · `,
      ));
      await refetchFn();
    } catch (err: any) {
      setError(getUploadErrorMessage(err, 'Reconciliation failed'));
    } finally {
      setReconciling(false);
    }
  };

  const handleReconcileBraintree = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    setReconciling(true);
    clearMessages();
    try {
      const { data } = await axios.post<BraintreeReconcileResult>(
        '/api/accounting/braintree-reconcile',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showResult(formatBraintreeReconcileResult(data));
      await refetchFn();
    } catch (err: any) {
      setError(getUploadErrorMessage(err, 'Braintree reconciliation failed'));
    } finally {
      setReconciling(false);
    }
  };

  // Stage 1 (preview) — mirrors the Revolut overwrite flow: dry-run for concrete
  // numbers, then open the in-app confirmation modal (never window.confirm).
  const handleReconcileBraintreeOverwrite = async (file: File) => {
    clearMessages();
    setOverwrite({
      kind: 'braintree',
      file,
      phase: 'previewing',
      title: 'Overwrite Braintree AIB reconciliation',
      pct: 0,
    });
    startProgress(estimateMs(estimateUnitsFromFile(file)));
    try {
      const previewForm = new FormData();
      previewForm.append('file', file);
      const { data: preview } = await axios.post<BraintreeReconcileResult>(
        '/api/accounting/braintree-reconcile?overwrite=true&dryRun=true',
        previewForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const linksToWipe = preview.priorLinksWiped ?? 0;
      const txnsToWipe = preview.priorTransactionsWiped ?? 0;
      const paymentsToReset = preview.priorPaymentsReset ?? 0;
      const fromLabel = preview.overwriteDateFrom ? preview.overwriteDateFrom.slice(0, 10) : '?';
      const toLabel = preview.overwriteDateTo ? preview.overwriteDateTo.slice(0, 10) : '?';
      const previewOrders = preview.ordersLinked ?? 0;
      const previewPayments = preview.paymentsMatched ?? 0;
      const previewUnclaimed = preview.unclaimedRows ?? 0;
      const previewRowsParsed = preview.rowsParsed ?? 0;
      const previewRefunds = preview.refundsParsed ?? 0;
      const previewAdjustments = preview.feeOnlyParsed ?? 0;
      const previewAdjustmentsNet = preview.feeOnlyNet ?? 0;
      const previewAlready = preview.ordersAlreadyLinked ?? 0;
      const previewConflicts = preview.ordersConflictingLink ?? 0;
      const previewNotFound = preview.ordersNotFound ?? 0;
      const assignmentLines = (preview.paymentAssignments ?? [])
        .slice(0, 5)
        .map((a) => `#${a.paymentId} · ${a.rowCount} txn(s) · ${a.ordersLinked} order(s)`)
        .join('\n');
      const assignmentBlock = assignmentLines ? `\n\nPer payment:\n${assignmentLines}` : '';

      const message =
        `Overwrite Braintree AIB reconciliation (${fromLabel} → ${toLabel})\n\n` +
        `This will DELETE ${linksToWipe} order link(s) and ${txnsToWipe} braintree_transactions row(s),\n` +
        `and RESET ${paymentsToReset} payment(s) (including any manually-linked ones), then re-import the CSV.\n\n` +
        `Re-import preview: ${previewRowsParsed} CSV row(s) (${previewRefunds} refund(s)), ` +
        `${previewOrders} order(s) would be linked, ${previewPayments} payment(s) matched, ` +
        `${previewUnclaimed} unclaimed.` +
        (previewAlready > 0 ? ` ${previewAlready} already linked.` : '') +
        (previewConflicts > 0 ? ` ${previewConflicts} linked to another payment.` : '') +
        (previewNotFound > 0 ? ` ${previewNotFound} orders not found.` : '') +
        (previewAdjustments > 0
          ? ` ${previewAdjustments} AIB adjustment(s) totalling ${previewAdjustmentsNet.toFixed(2)}.`
          : '') +
        `${assignmentBlock}` +
        previewProblemBlock(braintreeReconcileProblems(preview));

      // CSV row count is the best available proxy for the re-import workload.
      const estUnits = previewRowsParsed || txnsToWipe || estimateUnitsFromFile(file);
      stopProgress();
      setOverwrite((prev) => (prev ? { ...prev, phase: 'confirm', message, estUnits, pct: 0 } : prev));
    } catch (err: any) {
      stopProgress();
      setOverwrite(null);
      setError(getUploadErrorMessage(err, 'Braintree reconciliation failed'));
    }
  };

  // Stage 2 (actual run): executed after the user confirms in the modal.
  const runBraintreeOverwrite = async (file: File) => {
    setReconciling(true);
    clearMessages();
    try {
      const liveForm = new FormData();
      liveForm.append('file', file);
      const { data } = await axios.post<BraintreeReconcileResult>(
        '/api/accounting/braintree-reconcile?overwrite=true',
        liveForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      showResult(formatBraintreeReconcileResult(data, [
        `overwrite wiped ${data.priorLinksWiped ?? 0} link(s), ${data.priorTransactionsWiped ?? 0} txn(s), reset ${data.priorPaymentsReset ?? 0} payment(s)`,
      ]));
      await refetchFn();
    } catch (err: any) {
      setError(getUploadErrorMessage(err, 'Braintree reconciliation failed'));
    } finally {
      setReconciling(false);
    }
  };

  const handleReconcilePaypal = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    setReconciling(true);
    clearMessages();
    try {
      const { data } = await axios.post<PaypalReconcileResult>(
        '/api/accounting/paypal-reconcile',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showResult(formatPaypalReconcileResult(data));
      await refetchFn();
    } catch (err: any) {
      setError(err.response?.data?.error || 'PayPal reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const handleReconcileVasco = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    setReconciling(true);
    clearMessages();
    try {
      const { data } = await axios.post<VascoReconcileResult>(
        '/api/accounting/vasco-reconcile',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showResult(formatVascoReconcileResult(data));
      await refetchFn();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Vasco reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const handleBankTransferMatch = async () => {
    setMatching(true);
    clearMessages();
    try {
      showResult(await runBankTransferMatch());
      await refetchFn();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Bank transfer matching failed');
    } finally {
      setMatching(false);
    }
  };

  const handleBackfillOrders = async () => {
    setBackfilling(true);
    clearMessages();
    try {
      const { data } = await axios.post<SyncOrdersResponse>('/api/b2b/sync-orders', { after: BACKFILL_AFTER });
      showResult(formatOrderSyncMessage(data, `Backfill from ${BACKFILL_START_LABEL}: `));
      await refetchFn();
    } catch (err: any) {
      setError(getBackfillErrorMessage(err));
    } finally {
      setBackfilling(false);
    }
  };

  const runOrderSync = async (): Promise<ResultMessage> => {
    const result = await handleSyncOrders();
    if (!result || !result.success) throw new Error((result as any)?.error || 'Orders sync failed');
    return formatOrderSyncMessage(result);
  };

  // "Sync" runs all automatic steps in this order. A step that fails does not
  // stop the next steps, because each step only reads data that is already
  // saved. The message has one line per step, and a step's problems follow on
  // their own lines. It is an error message when any step failed and a
  // warning when any step left a problem, so neither is ever shown in green.
  const handleSync = async () => {
    setSyncingAll(true);
    clearMessages();
    const lines: string[] = [];
    let failed = false;
    let hasProblems = false;
    const step = async (label: string, run: () => Promise<string | ResultMessage>) => {
      try {
        const result = await run();
        const { message, warning: stepWarning } = typeof result === 'string' ? { message: result, warning: false } : result;
        if (stepWarning) hasProblems = true;
        lines.push(`${label}: ${message}`);
      } catch (err: any) {
        failed = true;
        lines.push(`${label} failed: ${err?.response?.data?.error || err?.message || 'unknown error'}`);
      }
    };
    try {
      await step('Orders', runOrderSync);
      await step('Revolut payments', async () => {
        if (revolutApi?.enabled !== true) {
          return 'skipped, the Revolut API is not switched on. Use "Reconcile ▾ → Revolut statement (XML file)".';
        }
        return runRevolutApiImport();
      });
      await step('Bank transfers', runBankTransferMatch);
      await step('Revolut cards', async () => {
        if (revolutMerchantApi?.enabled !== true) {
          return 'skipped, the Revolut Merchant API is not switched on. Use "Reconcile ▾ → Revolut CC".';
        }
        return runRevolutMerchantImport();
      });
      await step('USD rates', runGkRatesSync);
      if (failed) setError(lines.join('\n'));
      else if (hasProblems) setWarning(lines.join('\n'));
      else setSuccess(lines.join('\n'));
      await refetchFn();
    } finally {
      setSyncingAll(false);
      loadRevolutApiStatus();
      loadRevolutMerchantApiStatus();
    }
  };

  const confirmOverwrite = async () => {
    const p = overwrite;
    if (!p || p.phase !== 'confirm') return;
    setOverwrite({ ...p, phase: 'overwriting', pct: 0 });
    startProgress(estimateMs(p.estUnits ?? estimateUnitsFromFile(p.file)));
    try {
      if (p.kind === 'revolut') await runRevolutOverwrite(p.file);
      else await runBraintreeOverwrite(p.file);
    } finally {
      stopProgress();
      setOverwrite(null);
    }
  };

  const cancelOverwrite = () => {
    const wasConfirm = overwrite?.phase === 'confirm';
    stopProgress();
    setOverwrite(null);
    if (wasConfirm) setSuccess('Overwrite cancelled — nothing changed.');
  };

  return {
    uploading,
    reconciling,
    matching,
    // True while any step of "Sync" runs, not only the orders step.
    syncing: syncing || syncingAll,
    backfilling,
    success,
    warning,
    error,
    setSuccess,
    setWarning,
    setError,
    revolutApi,
    revolutMerchantApi,
    handleRevolutMerchantImport,
    handleUploadXml,
    handleReconcileRevolut,
    handleReconcileRevolutOverwrite,
    handleReconcileBraintree,
    handleReconcileBraintreeOverwrite,
    handleReconcilePaypal,
    handleReconcileVasco,
    handleBankTransferMatch,
    handleSync,
    handleBackfillOrders,
    overwrite,
    confirmOverwrite,
    cancelOverwrite,
  };
}
